import { useQuery } from '@tanstack/react-query';
import { ContextFn, addHours, differenceInMinutes, isAfter, isBefore, roundToNearestHours, subHours } from 'date-fns';
import { assign, identity } from 'lodash';
import { useMemo } from 'react';

import { GetWaveSpectrumDataRawResponse, WaveSpectrumDataByTimestamp } from '@/api/historicalData';
import { dateRangeParams } from '@/api/historicalData/helpers';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { useViewToken } from '@/context/ViewTokenContext';
import { useTzContext } from '@/hooks/useTzContext';
import { DateRange, useGlobalStore } from '@/store';
import { findClosestByBinarySearch, sensorDataStaleTime } from '@/utils';
import { generateMockWaveSpectrumResponse } from '@/utils/mockDataHelpers';

type GetHistoricalWaveSpectrumDataParams = { spotterId: string; dateRange: DateRange };

export const getHistoricalWaveSpectrumData = async (
    { spotterId, dateRange }: GetHistoricalWaveSpectrumDataParams,
    options?: RequestOptions
): Promise<WaveSpectrumDataByTimestamp> => {
    const { waveSpectrum } = useGlobalStore.getState().mockDataSwitches;
    if (waveSpectrum) {
        return generateMockWaveSpectrumResponse(dateRange);
    }

    const { data } = await axios.get<GetWaveSpectrumDataRawResponse, GetWaveSpectrumDataRawResponse>(
        '/api/spotter-wave-data',
        {
            params: {
                spotterId,
                ...dateRangeParams(dateRange, 'exclusive'),
                includeWaves: false,
                includeFrequencyData: true,
                includeDirectionalMoments: true,
                processingSources: 'all',
            },
            ...requestConfig(options),
        }
    );

    if (data.frequencyData.length === 100) {
        console.warn('Received maximum result count for wave frequency data; samples may be missing');
    }

    const dataByTimestamp = data.frequencyData.reduce<WaveSpectrumDataByTimestamp>((acc, item) => {
        const { frequency, direction, varianceDensity, a1, b1, a2, b2, timestamp, df } = item;
        return {
            ...acc,
            [timestamp]: { frequency, direction, varianceDensity, a1, b1, a2, b2, df },
        };
    }, {});
    return dataByTimestamp;
};

type UseWaveSpectrumDataPageOptions = {
    config?: { enabled?: boolean };
    spotterId: string;
    timestamp?: Date;
};

const pageDateRange = (timestamp: Date | undefined | null, tzContext: { in: ContextFn<Date> | undefined }) => {
    if (!timestamp) {
        return undefined;
    }
    // the API call can only return 100 samples at a time, which in most sampling intervals is a few days worth of data,
    // but could be as little as just under 8 hours with (12*[5-minute hdr] + 1*[1 hour waves:spectrum]) samples per hour.
    const endDate = roundToNearestHours(timestamp, { ...tzContext, nearestTo: 6, roundingMethod: 'ceil' });
    return { endDate, startDate: subHours(endDate, 6) };
};

/**
 * Returns Spotter wave data including frequency and directional moment data for a page of data surrounding timestamp. The page is
 * 6 hours in an effort to stay under the sample limit, but with rounded boundaries to help the cache. If there is a sample for the
 * timestamp, it should be included in the page.
 */
export const useWaveSpectrumDataPage = ({
    config: { enabled = true } = {},
    spotterId,
    timestamp,
}: UseWaveSpectrumDataPageOptions) => {
    const tz = useTzContext();
    const { loading: loadingToken, viewToken } = useViewToken();

    const dateRange = pageDateRange(timestamp, tz);
    return useQuery<WaveSpectrumDataByTimestamp>({
        queryKey: [
            'historical-wave-spectrum-data-page',
            spotterId,
            dateRangeParams(dateRange),
            { isPublic: Boolean(viewToken) },
        ],
        queryFn: ({ signal }) =>
            getHistoricalWaveSpectrumData({ spotterId, dateRange: dateRange! }, { viewToken, signal }),
        staleTime: sensorDataStaleTime(dateRange),
        enabled: Boolean(enabled && spotterId && dateRange && !loadingToken),
    });
};

export type UseWaveSpectrumSampleOptions = UseWaveSpectrumDataPageOptions & { samplePeriodHours: number };

/**
 * Returns Spotter wave data including frequency and directional moment data for the closest sample to the given timestamp, if there are any within samplePeriodHours.
 */
export const useWaveSpectrumSample = ({
    config,
    spotterId,
    timestamp,
    samplePeriodHours,
}: UseWaveSpectrumSampleOptions) => {
    const tz = useTzContext();
    const { data: timestampPage, isPending: isTimestampPagePending } = useWaveSpectrumDataPage({
        config,
        spotterId,
        timestamp,
    });

    const adjacentPageOptions = useMemo(() => {
        // slightly awkward: we want to keep our page fetches on consistent boundaries to allow caching, but if our requested timestamp is within
        // a sample period of the edge, our closest sample might be on the next page. in that case, we'll make a second request and combine the results
        // to find our candidate.
        const dateRange = pageDateRange(timestamp, tz);
        if (dateRange && timestamp) {
            const { startDate, endDate } = dateRange;
            if (isBefore(timestamp, addHours(startDate, samplePeriodHours))) {
                return {
                    spotterId,
                    timestamp: subHours(startDate, 1),
                    config,
                };
            }
            if (isAfter(timestamp, subHours(endDate, samplePeriodHours))) {
                return {
                    spotterId,
                    timestamp: addHours(endDate, 1),
                    config,
                };
            }
        }
        // timestamp is comfortably in the middle of the page if present, no need for adjacent page
        return {
            spotterId,
            timestamp,
            config: {
                ...config,
                enabled: false,
            },
        };
    }, [config, samplePeriodHours, spotterId, timestamp, tz]);

    const { data: adjacentPage, isPending: isAdjacentPagePending } = useWaveSpectrumDataPage(adjacentPageOptions);
    const usingAdjacentPage = adjacentPageOptions.config?.enabled !== false;

    const isPending = isTimestampPagePending || (isAdjacentPagePending && usingAdjacentPage);

    const data = useMemo(() => {
        if (isPending) {
            return undefined;
        }
        if (!usingAdjacentPage) {
            return timestampPage;
        }
        return assign({}, adjacentPage, timestampPage);
    }, [adjacentPage, isPending, timestampPage, usingAdjacentPage]);

    return useMemo(() => {
        if (isPending || !timestamp || !data) {
            return { isPending: true };
        }

        const timestamps = Object.keys(data)
            .sort()
            .map((ts) => new Date(ts).getTime());

        const selectedTime = timestamp.getTime();
        const closestTs = findClosestByBinarySearch(timestamps, selectedTime, identity);
        if (closestTs && Math.abs(differenceInMinutes(timestamp, closestTs)) <= samplePeriodHours * 60) {
            const sampleTimestamp = new Date(closestTs).toISOString();
            return { isPending, data: { timestamp: sampleTimestamp, waveSpectrumSample: data[sampleTimestamp] } };
        }
        return { isPending, data: { timestamp: timestamp.toISOString(), waveSpectrumSample: null } };
    }, [data, isPending, samplePeriodHours, timestamp]);
};

export const waveSpectrumExportForTests = { pageDateRange };
